import { captureException } from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { activeSignerQueryParams } from '../../hooks/useActiveSigner';
import { useWalletStorage } from '../../hooks/useWalletStorage';
import { isUserRejectedError } from '../../lib/errors';
import { filterQueryKey, invalidateQueries } from '../../lib/reactQueryHelpers';
import { showToast } from '../../lib/showToast';
import { linkSigner, PollSignerParams, pollSignerStatus, revokeSigner } from '../../lib/signer';
import {
  disableOneClickSigningFailedToastMessage,
  enableOneClickSigningFailedToastMessage,
  userRejectedSignatureToastMessage,
} from '../../lib/toasts';
import { assertRequiredData } from '../../lib/typeGuards';

export const useNewSigner = (key: 'enable' | 'renew') => {
  const { updateSubaccountData } = useWalletStorage();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['linkNewSigner', key],
    mutationFn: async (params: Omit<PollSignerParams, 'signerAddress'>) => {
      assertRequiredData(params);
      const { signerAddress, signerPrivateKey } = await linkSigner(params);

      await pollSignerStatus({
        queryClient,
        params: { ...params, signerAddress },
        isRevoke: false,
      });
      await queryClient.invalidateQueries({
        queryKey: filterQueryKey(activeSignerQueryParams(params.subaccountId).queryKey),
        refetchType: 'all',
      });

      updateSubaccountData(params.address, params.subaccountId, {
        signerKey: signerPrivateKey,
        signingMethod: 'one-click',
        linked: true,
      });
    },
    onError: (error) => {
      if (isUserRejectedError(error)) {
        const action = 'enable One-Click Trading';
        showToast(userRejectedSignatureToastMessage(action));
        return;
      }
      if (error.message.includes('Maximum linked signers')) {
        showToast(enableOneClickSigningFailedToastMessage(error.message));
        return;
      }
      captureException(error);
      showToast(enableOneClickSigningFailedToastMessage());
    },
  });
};

export const useRevokeSigner = () => {
  const { updateSubaccountData } = useWalletStorage();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['revokeSigner'],
    mutationFn: async (params: PollSignerParams & { isActiveSigner: boolean }) => {
      assertRequiredData(params);
      await revokeSigner(params);
      await pollSignerStatus({ queryClient, params, isRevoke: true });
      const { queryKey } = activeSignerQueryParams(params.subaccountId);
      invalidateQueries(queryClient, queryKey);

      // Only set signing method to 'manual' if we're revoking the current active signer
      if (params.isActiveSigner) {
        updateSubaccountData(params.address, params.subaccountId, {
          signingMethod: 'manual',
        });
      }
    },
    onError: (error) => {
      if (isUserRejectedError(error)) {
        const action = 'disable One-Click Trading';
        showToast(userRejectedSignatureToastMessage(action));
        return;
      }
      captureException(error);
      showToast(disableOneClickSigningFailedToastMessage());
    },
  });
};
